import { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  LockKeyhole,
  Check,
} from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import {
  SUPIN_LICENSE_PAGES,
  verifySupinLicensePassword,
} from '@/config/supinLicense';

interface AccessBoxProps {
  /** True once the correct access password has been entered. */
  unlocked: boolean;
  onUnlockedChange: (unlocked: boolean) => void;
}

// ── Access box: password-gates the Generate button ──────────────────────────
// Rendered in-flow above the logo so it shares the hero's centered axis (and
// shifts left with it when the feature-tree sidebar is open). Red-themed to read
// as a restriction/warning.
export function SupinLicenseAccessBox({
  unlocked,
  onUnlockedChange,
}: AccessBoxProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  const submit = async () => {
    if (unlocked || checking) return;
    setChecking(true);
    const ok = await verifySupinLicensePassword(value);
    setChecking(false);
    if (ok) {
      setError(false);
      onUnlockedChange(true);
    } else {
      setError(true);
      onUnlockedChange(false);
    }
  };

  return (
    <div className="mb-6 w-full max-w-md">
      <div className="rounded-lg border-2 border-red-600 bg-black/85 px-5 py-3 text-center shadow-lg backdrop-blur-sm">
        <div className="flex items-center justify-center gap-2">
          {unlocked ? (
            <Check className="h-4 w-4 shrink-0 text-red-500" />
          ) : (
            <LockKeyhole className="h-4 w-4 shrink-0 text-red-500" />
          )}
          <span className="text-sm font-semibold text-red-500">
            SUPIN License Access
          </span>
        </div>

        <p className="mt-1.5 text-left text-xs leading-relaxed text-telekinesis-neutral-300">
          NASA License Agreement signed with Venom Industries, Inc. (DBA/FKA
          Teranis, Inc.) on 05/20/2026. Restricted access to Venom Personnel
          only. Source Code is locally hosted on Venom Servers. Accessible only
          by U.S. Persons on devices in the U.S.
        </p>

        {!unlocked && (
          <form
            className="mt-3 flex items-center justify-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <input
              type="password"
              value={value}
              autoComplete="off"
              placeholder="Access password"
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError(false);
              }}
              className={cn(
                'flex-1 rounded-md border bg-telekinesis-bg-dark px-3 py-1.5 text-sm text-telekinesis-neutral-50 outline-none transition-colors placeholder:text-telekinesis-neutral-500',
                error
                  ? 'border-red-500 focus:border-red-400'
                  : 'border-[#737373] focus:border-red-500',
              )}
            />
            <button
              type="submit"
              disabled={checking || value.length === 0}
              className="rounded-md border border-red-600 bg-red-950 px-3 py-1.5 text-sm font-medium text-red-300 transition-colors hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Unlock
            </button>
          </form>
        )}
        {error && !unlocked && (
          <p className="mt-1.5 text-xs text-red-500">
            Incorrect password — Generate stays disabled.
          </p>
        )}
        {unlocked && (
          <p className="mt-1.5 text-xs text-telekinesis-neutral-300">
            Access granted — Generate is enabled for this session.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Right: manual slideshow of the signed agreement ─────────────────────────
// Absolute side panel pinned to the right of the hero; rendered inside the
// (relative) <main>.
export function SupinLicenseSlideshow() {
  const [index, setIndex] = useState(0);
  const total = SUPIN_LICENSE_PAGES.length;

  // Keep index in range if the page list ever changes.
  useEffect(() => {
    if (index > total - 1) setIndex(total - 1);
  }, [index, total]);

  const go = (delta: number) =>
    setIndex((i) => Math.min(total - 1, Math.max(0, i + delta)));

  return (
    <div className="absolute right-3 top-10 z-[6] flex w-[33%] max-w-[35rem] flex-col rounded-lg border border-telekinesis-neutral-700 bg-black/85 p-3 shadow-lg backdrop-blur-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-telekinesis-neutral-200">
          NASA Software Usage Agreement
        </span>
        <span className="text-xs tabular-nums text-telekinesis-neutral-400">
          Page {index + 1} / {total}
        </span>
      </div>

      {/* Image area matches the pages' exact aspect ratio (1700×2200 = 17/22)
          so the box wraps the page tightly with no letterbox whitespace. */}
      <div className="aspect-[17/22] w-full overflow-hidden rounded bg-white">
        <img
          src={SUPIN_LICENSE_PAGES[index]}
          alt={`NASA SUPIN Software Usage Agreement, page ${index + 1} of ${total}`}
          className="h-full w-full object-contain"
        />
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={index === 0}
          className="flex items-center gap-1 rounded-md border border-telekinesis-neutral-700 px-3 py-1.5 text-sm text-telekinesis-neutral-100 transition-colors hover:border-telekinesis-gold disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </button>
        <div className="flex items-center gap-1.5">
          {SUPIN_LICENSE_PAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to page ${i + 1}`}
              onClick={() => setIndex(i)}
              className={cn(
                'h-1.5 w-1.5 rounded-full transition-colors',
                i === index
                  ? 'bg-telekinesis-gold'
                  : 'bg-telekinesis-neutral-600 hover:bg-telekinesis-neutral-400',
              )}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={index === total - 1}
          className="flex items-center gap-1 rounded-md border border-telekinesis-neutral-700 px-3 py-1.5 text-sm text-telekinesis-neutral-100 transition-colors hover:border-telekinesis-gold disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
